/**
 * Contexto que mantiene en memoria la lista de categorías (gasto e ingreso)
 * y la mantiene fresca tras crear / renombrar / borrar desde Configuración.
 *
 * Mientras la primera carga está en curso, expone los arrays hardcodeados
 * de `types.ts` como fallback para que los selectores nunca queden vacíos.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { fetchCategorias } from "../api";
import {
  CATEGORIAS_GASTO,
  CATEGORIAS_INGRESO,
  type Categoria,
} from "../types";
import { useAuth } from "../auth/AuthContext";

interface CategoriasState {
  gasto: string[];
  ingreso: string[];
  raw: Categoria[];
  loading: boolean;
  error: string | null;
}

interface CategoriasContextValue extends CategoriasState {
  reload: () => Promise<void>;
}

const FALLBACK: CategoriasState = {
  gasto: [...CATEGORIAS_GASTO],
  ingreso: [...CATEGORIAS_INGRESO],
  raw: [],
  loading: true,
  error: null,
};

const Ctx = createContext<CategoriasContextValue | null>(null);

export function CategoriasProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [state, setState] = useState<CategoriasState>(FALLBACK);

  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const rows = await fetchCategorias();
      const gasto = rows.filter((r) => r.tipo === "gasto").map((r) => r.nombre);
      const ingreso = rows.filter((r) => r.tipo === "ingreso").map((r) => r.nombre);
      setState({
        gasto: gasto.length ? gasto : [...CATEGORIAS_GASTO],
        ingreso: ingreso.length ? ingreso : [...CATEGORIAS_INGRESO],
        raw: rows,
        loading: false,
        error: null,
      });
    } catch (err) {
      setState({
        gasto: [...CATEGORIAS_GASTO],
        ingreso: [...CATEGORIAS_INGRESO],
        raw: [],
        loading: false,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }, []);

  useEffect(() => {
    if (status !== "authed") return;
    void reload();
  }, [status, reload]);

  const value = useMemo<CategoriasContextValue>(() => ({ ...state, reload }), [state, reload]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCategorias(): CategoriasContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useCategorias fuera de <CategoriasProvider>");
  return v;
}
